import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface SynopticParallelCardProps {
  parallel: {
    ref?: string;
    parallelRefs: string[];
    similarities?: string;
    differences: string;
  };
  bookName: string;
  setUrl: (url: string) => void;
}

/**
 * Shared component for displaying a single synoptic parallel
 * Used in both the scriptural context page and the capability modal
 */
export function SynopticParallelCard({ parallel, bookName, setUrl }: SynopticParallelCardProps) {
  const isDarkMode = useDarkMode();
  return (
    <div className="text-sm p-3 rounded border">
      {parallel.ref && (
        <div className="font-medium mb-2">
          {bookName} {parallel.ref}
        </div>
      )}
      {parallel.parallelRefs.length > 0 && (
        <div className="mb-2">
          <span className="font-medium">Parallels: </span>
          {parallel.parallelRefs.map((ref, i) => (
            <span key={i}>
              <span
                className={`cursor-pointer hover:underline ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                onClick={() => setUrl(`/${ref}`)}
              >
                {ref}
              </span>
              {i < parallel.parallelRefs.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      )}
      {parallel.similarities && parallel.similarities !== 'N/A' && (
        <div className="mb-2">
          <span className="font-medium">Similarities: </span>
          <span className="text-xs">{parallel.similarities}</span>
        </div>
      )}
      <div>
        <span className="font-medium">Differences: </span>
        <span className="text-xs">{parallel.differences}</span>
      </div>
    </div>
  );
}

interface SynopticParallelsListProps {
  parallels: SynopticParallelCardProps['parallel'][];
  bookName: string;
  setUrl: (url: string) => void;
}

/**
 * List wrapper for multiple synoptic parallels
 */
export function SynopticParallelsList({ parallels, bookName, setUrl }: SynopticParallelsListProps) {
  if (!parallels || parallels.length === 0) return null;

  return (
    <div className="space-y-3">
      {parallels.map((parallel, index) => (
        <SynopticParallelCard
          key={index}
          parallel={parallel}
          bookName={bookName}
          setUrl={setUrl}
        />
      ))}
    </div>
  );
}

